/**
 * The head of the Production Line detail page (production-lines): the
 * breadcrumb, the title with its status tag and meta line, the quoted
 * prompt, the primary action and the More menu (edit brief, edit line,
 * duplicate, pause, resume, archive). Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { Link, type NavigateFunction } from 'react-router-dom';

import type { WorkspaceCopy } from '../../merchant/copy.js';
import {
  formatRelative,
  lineActionLabel,
  lineStatusLabel,
  type LineSummary,
} from '../../merchant/model.js';
import {
  BackIcon,
  Button,
  ButtonLink,
  IconButton,
  LinkIcon,
  Menu,
  MoreIcon,
  SoonBadge,
  Tag,
  type useToast,
} from '../../merchant/ui/primitives.js';
import type { ConfiguredLine, LineModal } from './productionLineJourney.js';

export function ProductionLineDetailHeader({
  copy,
  language,
  titleId,
  productionLineId,
  title,
  status,
  domain,
  prompt,
  updatedAt,
  summary,
  realLine,
  busy,
  moreOpen,
  setMoreOpen,
  setModal,
  lifecycle,
  navigate,
  toast,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly titleId: string;
  readonly productionLineId: string;
  readonly title: string;
  readonly status: LineSummary['status'];
  readonly domain: string;
  readonly prompt: string;
  readonly updatedAt: string | null;
  readonly summary: LineSummary | null;
  readonly realLine: ConfiguredLine | null;
  readonly busy: string | null;
  readonly moreOpen: boolean;
  readonly setMoreOpen: Dispatch<SetStateAction<boolean>>;
  readonly setModal: Dispatch<SetStateAction<LineModal>>;
  readonly lifecycle: (action: 'pause' | 'resume' | 'archive') => void;
  readonly navigate: NavigateFunction;
  readonly toast: ReturnType<typeof useToast>;
}): ReactNode {
  const primaryAction = (): ReactNode => {
    if (summary === null) return null;
    if (summary.status === 'paused' && realLine !== null) {
      return (
        <Button
          variant="primary"
          disabled={busy !== null}
          onClick={() => {
            lifecycle('resume');
          }}
        >
          {copy.line.resume}
        </Button>
      );
    }
    if (summary.action === 'videos') {
      return (
        <ButtonLink
          to={`/videos/line/${encodeURIComponent(productionLineId)}`}
          variant="primary"
        >
          {lineActionLabel('videos', copy)}
        </ButtonLink>
      );
    }
    if (summary.action === 'brief') {
      return (
        <Button
          variant="primary"
          onClick={() => {
            setModal('brief');
          }}
        >
          {lineActionLabel('brief', copy)}
        </Button>
      );
    }
    const target =
      summary.action === 'analysis'
        ? `/production-lines/${encodeURIComponent(productionLineId)}/analysis`
        : `/production-lines/${encodeURIComponent(productionLineId)}`;
    return (
      <ButtonLink to={target} variant="primary">
        {lineActionLabel(summary.action, copy)}
      </ButtonLink>
    );
  };

  return (
    <>
      <nav
        aria-label={copy.line.breadcrumb}
        className="mj-ws-crumb mj-ws-muted mj-ws-sm"
      >
        <Link to="/production-lines">
          <BackIcon />
          {copy.lines.title}
        </Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{title}</span>
      </nav>

      <div className="mj-ws-phead mj-ws-detail-head">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="mj-ws-row" style={{ gap: 12 }}>
            <h1 id={titleId} className="mj-ws-h1">
              {title || copy.common.loading}
            </h1>
            <Tag status={status}>{lineStatusLabel(status, copy)}</Tag>
          </div>
          <div className="mj-ws-muted mj-ws-sm mj-ws-row mj-ws-detail-meta">
            {domain ? (
              <span>
                <LinkIcon />
                <span dir="ltr">{domain}</span>
              </span>
            ) : null}
            {realLine !== null ? (
              <span>{copy.line.cadence(realLine.productionLine.videosPerDay)}</span>
            ) : null}
            {updatedAt !== null ? (
              <span className="mj-ws-tnum">
                {copy.common.updated(formatRelative(updatedAt, copy, language))}
              </span>
            ) : null}
          </div>
          {prompt ? <p className="mj-ws-detail-prompt">“{prompt}”</p> : null}
        </div>
        <div className="mj-ws-row mj-ws-actions" data-more="1">
          {primaryAction()}
          <IconButton
            label={copy.line.more}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => {
              setMoreOpen((open) => !open);
            }}
          >
            <MoreIcon />
          </IconButton>
          <Menu
            open={moreOpen}
            onClose={() => {
              setMoreOpen(false);
            }}
            label={copy.line.more}
            align="end"
            className="mj-ws-menu-more"
            items={[
              {
                key: 'brief',
                label: copy.line.editBrief,
                onSelect: () => {
                  setMoreOpen(false);
                  setModal('brief');
                },
              },
              ...(realLine !== null && realLine.editor.available
                ? [
                    {
                      key: 'edit',
                      label: copy.line.editLine,
                      onSelect: () => {
                        setMoreOpen(false);
                        void navigate(
                          `/production-lines/${encodeURIComponent(productionLineId)}/edit`,
                        );
                      },
                    },
                  ]
                : []),
              {
                key: 'duplicate',
                label: copy.line.duplicate,
                trailing: <SoonBadge />,
                onSelect: () => {
                  setMoreOpen(false);
                  toast(copy.line.duplicateSoon);
                },
              },
              ...(realLine !== null && realLine.productionLine.state === 'ACTIVE'
                ? [
                    {
                      key: 'pause',
                      label: copy.line.pause,
                      onSelect: () => {
                        setMoreOpen(false);
                        setModal('pause');
                      },
                    },
                  ]
                : []),
              ...(realLine !== null && realLine.productionLine.state === 'PAUSED'
                ? [
                    {
                      key: 'resume',
                      label: copy.line.resume,
                      onSelect: () => {
                        setMoreOpen(false);
                        lifecycle('resume');
                      },
                    },
                  ]
                : []),
              {
                key: 'archive',
                label: copy.line.archive,
                disabled:
                  realLine === null || realLine.productionLine.state === 'ARCHIVED',
                onSelect: () => {
                  setMoreOpen(false);
                  setModal('archive');
                },
              },
            ]}
          />
        </div>
      </div>
    </>
  );
}
